/**
 * Passkeys on someone else's account, rendered on the Eyes-Only tab beside MFA reset.
 *
 * Who can act follows the MFA-reset rule, enforced by the API: system Owners and Admins on ordinary
 * accounts, only an Owner on a system account, nobody on a system Owner. Removing a passkey here
 * signs the account holder out everywhere and emails them, which is why it has its own confirmation.
 * Removed passkeys stay on record and are listed apart, read-only, with where each was added from.
 * Your own passkeys live on the Security tab, so this renders nothing on your own account.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { Button, Label, Loader, Message, Table } from 'semantic-ui-react';
import moment from 'moment';

import { useAppSelector } from '../../../hooks/redux/useApp';
import ConfirmationModal from '../../../components/common/confirmationModal';
import { TemplateHeader } from '../../../components/template/templateHeader';
import { describeDevice } from '../../../helpers/passkeys';
import {
  AccountPasskey,
  deleteAllPasskeys,
  deletePasskey,
  fetchPasskeys
} from './passkeys/passkeysApi';
import styles from './passkeys/PasskeysPanel.module.css';

import { i18nKeys } from '../constants';

const columnPrefix = 'account.account-security.passkeys';

const formatDate = (value: string | null) =>
  value
    ? moment.utc(value).local().format('D MMM YYYY, h:mm a')
    : I18n.t(`${columnPrefix}.never`);

/** The address and browser a passkey was added from, when the row recorded them. */
const AddedFrom = ({ passkey }: { passkey: AccountPasskey }) => {
  const device = describeDevice(passkey.userAgent ?? '');
  const browser =
    device.browser && device.os
      ? I18n.t(i18nKeys.PASSKEYS_BROWSER_ON_OS, {
          browser: device.browser,
          os: device.os
        })
      : (device.browser ?? device.os);
  return (
    <span title={passkey.userAgent ?? undefined}>
      {passkey.ipAddress && <span>{passkey.ipAddress}</span>}
      {browser && <span className={styles.addedFrom}>{browser}</span>}
    </span>
  );
};

interface PasskeysSupportProps {
  accountId?: number;
}

const PasskeysSupport = ({ accountId }: PasskeysSupportProps) => {
  const sessionAccountId = useAppSelector((state) => state.session.account?.id);
  const isOwn = !!accountId && Number(accountId) === Number(sessionAccountId);
  const [passkeys, setPasskeys] = useState<AccountPasskey[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AccountPasskey | null>(null);
  const [removingAll, setRemovingAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) {
      return;
    }
    setLoading(true);
    setFailed(false);
    try {
      setPasskeys(await fetchPasskeys(accountId, true));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (!isOwn) {
      load();
    }
  }, [isOwn, load]);

  if (isOwn) {
    return null;
  }

  const live = passkeys.filter((passkey) => !passkey.removedAt);
  const removed = passkeys.filter((passkey) => passkey.removedAt);

  const run = async (action: () => Promise<unknown>, noticeKey: string) => {
    if (!accountId) {
      return;
    }
    setBusy(true);
    setFailed(false);
    try {
      await action();
      setNotice(noticeKey);
      await load();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = () => {
    const passkey = removing;
    setRemoving(null);
    if (passkey && accountId) {
      run(
        () => deletePasskey(accountId, passkey.id),
        i18nKeys.PASSKEYS_REMOVED
      );
    }
  };

  const handleRemoveAll = () => {
    setRemovingAll(false);
    if (accountId) {
      run(() => deleteAllPasskeys(accountId), i18nKeys.PASSKEYS_REMOVED_ALL);
    }
  };

  return (
    <div className="passkeys-support" style={{ marginTop: '24px' }}>
      <div className="template-heading">
        <TemplateHeader as="h3" label={i18nKeys.PASSKEYS_HEADER} />
      </div>

      <div style={{ width: '93.75%' }}>
        {notice && (
          <Message positive size="small" onDismiss={() => setNotice(null)}>
            <Message.Content>{I18n.t(notice)}</Message.Content>
          </Message>
        )}
        {failed && (
          <Message negative size="small" role="alert">
            <Message.Content>
              {I18n.t('general.general-error-message')}
            </Message.Content>
          </Message>
        )}

        {loading && <Loader active inline="centered" />}
        {!loading && live.length === 0 && (
          <Message info>
            <Message.Content>{I18n.t(i18nKeys.PASSKEYS_NONE)}</Message.Content>
          </Message>
        )}
        {!loading && live.length > 0 && (
          <>
            <Message info>
              <Message.Content>
                {I18n.t(i18nKeys.PASSKEYS_INSTRUCTIONS)}
              </Message.Content>
            </Message>
            <Table basic="very" unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {I18n.t(`${columnPrefix}.columns.name`)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {I18n.t(`${columnPrefix}.columns.added`)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {I18n.t(`${columnPrefix}.columns.last-used`)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {I18n.t(`${columnPrefix}.columns.type`)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {I18n.t(i18nKeys.PASSKEYS_COLUMN_ADDED_FROM)}
                  </Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {live.map((passkey) => (
                  <Table.Row key={passkey.id}>
                    <Table.Cell>{passkey.name}</Table.Cell>
                    <Table.Cell>{formatDate(passkey.createdAt)}</Table.Cell>
                    <Table.Cell>{formatDate(passkey.lastUsedAt)}</Table.Cell>
                    <Table.Cell>
                      <Label size="small" className={styles.typeLabel}>
                        {I18n.t(
                          `${columnPrefix}.${passkey.isBackupEligible ? 'synced' : 'device'}`
                        )}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <AddedFrom passkey={passkey} />
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <Button
                        basic
                        negative
                        size="small"
                        disabled={busy}
                        aria-label={I18n.t(i18nKeys.PASSKEYS_REMOVE_NAMED, {
                          name: passkey.name
                        })}
                        onClick={() => setRemoving(passkey)}
                      >
                        {I18n.t(i18nKeys.PASSKEYS_REMOVE_BUTTON)}
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            <Button
              negative
              disabled={busy}
              loading={busy}
              onClick={() => setRemovingAll(true)}
            >
              {I18n.t(i18nKeys.PASSKEYS_REMOVE_ALL_BUTTON)}
            </Button>
          </>
        )}
        {!loading && removed.length > 0 && (
          <div className={styles.removedSection}>
            <h4>{I18n.t(i18nKeys.PASSKEYS_REMOVED_HEADER)}</h4>
            <p className={styles.instructions}>
              {I18n.t(i18nKeys.PASSKEYS_REMOVED_INSTRUCTIONS)}
            </p>
            <Table basic="very" unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {I18n.t(`${columnPrefix}.columns.name`)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {I18n.t(`${columnPrefix}.columns.added`)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {I18n.t(i18nKeys.PASSKEYS_COLUMN_REMOVED)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {I18n.t(i18nKeys.PASSKEYS_COLUMN_ADDED_FROM)}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {removed.map((passkey) => (
                  <Table.Row key={passkey.id} className={styles.removedRow}>
                    <Table.Cell>{passkey.name}</Table.Cell>
                    <Table.Cell>{formatDate(passkey.createdAt)}</Table.Cell>
                    <Table.Cell>{formatDate(passkey.removedAt)}</Table.Cell>
                    <Table.Cell>
                      <AddedFrom passkey={passkey} />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <ConfirmationModal
        open={!!removing}
        title={I18n.t(i18nKeys.PASSKEYS_CONFIRM_TITLE)}
        content={I18n.t(i18nKeys.PASSKEYS_CONFIRM_MESSAGE, {
          name: removing?.name ?? ''
        })}
        onClose={() => setRemoving(null)}
        onSubmit={handleRemove}
      />
      <ConfirmationModal
        open={removingAll}
        title={I18n.t(i18nKeys.PASSKEYS_REMOVE_ALL_CONFIRM_TITLE)}
        content={I18n.t(i18nKeys.PASSKEYS_REMOVE_ALL_CONFIRM_MESSAGE)}
        onClose={() => setRemovingAll(false)}
        onSubmit={handleRemoveAll}
      />
    </div>
  );
};

export default PasskeysSupport;
